import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, TrendingUp, TrendingDown, AlertCircle, FileDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface PortfolioReport {
  totalInvestments: number;
  totalValue: number;
  avgESGScore: number;
  highPerformers: number;
  lowPerformers: number;
  riskAlerts: number;
  topSectors: string[];
  currency: string;
  sectorBreakdown: any[];
  esgBreakdown: any[];
  investmentTypes: any[];
  riskAnalysis: any[];
}

export const PortfolioReporting: React.FC = () => {
  const [reportData, setReportData] = useState<PortfolioReport>({
    totalInvestments: 0,
    totalValue: 0,
    avgESGScore: 0,
    highPerformers: 0,
    lowPerformers: 0,
    riskAlerts: 0,
    topSectors: [],
    currency: 'USD',
    sectorBreakdown: [],
    esgBreakdown: [],
    investmentTypes: [],
    riskAnalysis: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfQuality, setPdfQuality] = useState<'standard' | 'compact'>('standard');
  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();
  const sectorChartRef = useRef<HTMLDivElement | null>(null);
  const esgChartRef = useRef<HTMLDivElement | null>(null);
  const invTypeChartRef = useRef<HTMLDivElement | null>(null);
  const sectorValueChartRef = useRef<HTMLDivElement | null>(null);
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch investments from Supabase
      const { data: investments, error } = await supabase
        .from('portfolio_investments')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (error) throw error;

      if (investments && investments.length > 0) {
        const totalValue = investments.reduce((sum, inv) => sum + Number(inv.investment_amount), 0);
        const avgESGScore = investments.reduce((sum, inv) => sum + (Number(inv.esg_score) || 0), 0) / investments.length;
        
        const highPerformers = investments.filter(inv => (Number(inv.esg_score) || 0) >= 80).length;
        const lowPerformers = investments.filter(inv => (Number(inv.esg_score) || 0) < 60).length;
        const riskAlerts = investments.filter(inv => 
          (Number(inv.carbon_intensity) || 0) > 500 || (Number(inv.esg_score) || 0) < 50
        ).length;

        const sectorCounts = investments.reduce((acc: any, inv) => {
          const sector = inv.sector || 'Unknown';
          acc[sector] = (acc[sector] || 0) + 1;
          return acc;
        }, {});

        const topSectors = Object.entries(sectorCounts)
          .sort(([,a], [,b]) => (b as number) - (a as number))
          .slice(0, 3)
          .map(([sector]) => sector);

        // Sector breakdown analysis
        const sectorBreakdown = investments.reduce((acc: any, inv) => {
          const sector = inv.sector || 'Unknown';
          if (!acc[sector]) {
            acc[sector] = {
              sector,
              totalValue: 0,
              avgESGScore: 0,
              count: 0,
              esgSum: 0
            };
          }
          acc[sector].totalValue += Number(inv.investment_amount);
          acc[sector].esgSum += Number(inv.esg_score) || 0;
          acc[sector].count += 1;
          acc[sector].avgESGScore = acc[sector].esgSum / acc[sector].count;
          return acc;
        }, {});

        // ESG breakdown
        const avgScores = investments.reduce((acc: any, inv) => {
          acc.environmental += Number(inv.environmental_score) || 0;
          acc.social += Number(inv.social_score) || 0;
          acc.governance += Number(inv.governance_score) || 0;
          return acc;
        }, { environmental: 0, social: 0, governance: 0 });

        const count = investments.length;
        const esgBreakdown = [
          { 
            name: 'Environmental', 
            value: count > 0 ? (avgScores.environmental / count).toFixed(1) : '0' 
          },
          { 
            name: 'Social', 
            value: count > 0 ? (avgScores.social / count).toFixed(1) : '0' 
          },
          { 
            name: 'Governance', 
            value: count > 0 ? (avgScores.governance / count).toFixed(1) : '0' 
          }
        ];

        // Investment types analysis
        const investmentTypes = investments.reduce((acc: any, inv) => {
          const type = inv.investment_type;
          if (!acc[type]) {
            acc[type] = { 
              name: type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' '), 
              value: 0, 
              count: 0 
            };
          }
          acc[type].value += Number(inv.investment_amount);
          acc[type].count += 1;
          return acc;
        }, {});

        // Risk analysis
        const riskAnalysis = investments.map(inv => ({
          name: inv.investment_name,
          esgScore: Number(inv.esg_score) || 0,
          carbonIntensity: Number(inv.carbon_intensity) || 0,
          riskLevel: (Number(inv.carbon_intensity) || 0) > 500 || (Number(inv.esg_score) || 0) < 50 ? 'High' : 
                    (Number(inv.esg_score) || 0) < 60 ? 'Medium' : 'Low'
        }));

        setReportData({
          totalInvestments: investments.length,
          totalValue,
          avgESGScore,
          highPerformers,
          lowPerformers,
          riskAlerts,
          topSectors,
          currency: investments[0]?.currency || 'USD',
          sectorBreakdown: Object.values(sectorBreakdown),
          esgBreakdown,
          investmentTypes: Object.values(investmentTypes),
          riskAnalysis
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate portfolio report",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateProfessionalPDF = async () => {
    if (!(await guardDownload())) return;

    setGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compressPdf: pdfQuality === 'compact' });
      
      // Set document properties
      pdf.setProperties({
        title: 'Portfolio ESG Report',
        subject: 'Comprehensive ESG Portfolio Analysis Report',
        author: 'Greencard Sustainability Manager',
        creator: 'Greencard Sustainability Manager'
      });

      let yPosition = 20;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);

      const captureAndAddChartImage = async (
        element: HTMLElement,
        currentY: number
      ): Promise<number> => {
        try {
          // Allow chart render to settle
          await new Promise((res) => setTimeout(res, 200));
          const canvas = await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: pdfQuality === 'compact' ? 2 : 3,
            useCORS: true,
            allowTaint: true,
            logging: false
          });
          const imgData = pdfQuality === 'compact'
            ? canvas.toDataURL('image/jpeg', 0.8)
            : canvas.toDataURL('image/png');
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          if (currentY + imgHeight > 270) {
            pdf.addPage();
            currentY = 20;
          }
          pdf.addImage(imgData, pdfQuality === 'compact' ? 'JPEG' : 'PNG', margin, currentY, imgWidth, imgHeight, undefined, pdfQuality === 'compact' ? 'FAST' : undefined);
          return currentY + imgHeight + 10;
        } catch (err) {
          pdf.setFontSize(10);
          pdf.text('Chart unavailable for embedding.', margin, currentY);
          return currentY + 12;
        }
      };

      // Modern Header Design
      pdf.setFillColor(34, 197, 94); // Green background
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Logo/Title area
      pdf.setFontSize(24);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'bold');
      pdf.text('Greencard', margin, 25);
      
      // Subtitle
      pdf.setFontSize(16);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'normal');
      pdf.text('Portfolio ESG Report', margin + 45, 25);
      
      // Date
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin + 45, 32);
      
      yPosition = 50;

      // Table of Contents
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Table of Contents', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const tocItems = [
        '1. Introduction',
        '2. Executive Summary',
        '3. Data Review',
        '4. Data Analysis',
        '5. Discussion of Analysed Data',
        '6. Conclusion',
        '7. Recommendations'
      ];

      tocItems.forEach(item => {
        pdf.text(item, margin, yPosition);
        yPosition += 5;
      });

      yPosition += 15;

      // 1. Introduction
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('1. Introduction', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const introductionText = [
        'This comprehensive ESG Portfolio Report provides a detailed analysis of the investment portfolio\'s environmental, social, and governance performance.',
        `The report examines ${reportData.totalInvestments} active investments with a total portfolio value of ${reportData.totalValue.toLocaleString()} ${reportData.currency}.`,
        'The analysis focuses on ESG scoring, sector distribution, risk assessment, and sustainability performance metrics.',
        'This report serves as a foundation for informed decision-making regarding portfolio optimization and ESG strategy development.'
      ];

      introductionText.forEach(line => {
        const lines = pdf.splitTextToSize(line, contentWidth);
        lines.forEach(textLine => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(textLine, margin, yPosition);
          yPosition += 5;
        });
        yPosition += 2;
      });

      yPosition += 15;

      // 2. Executive Summary
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('2. Executive Summary', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const executiveSummary = [
        'Portfolio Overview:',
        `• Total Investments: ${reportData.totalInvestments}`,
        `• Portfolio Value: ${reportData.totalValue.toLocaleString()} ${reportData.currency}`,
        `• Average ESG Score: ${reportData.avgESGScore.toFixed(1)}`,
        `• High Performers (ESG ≥ 80): ${reportData.highPerformers}`,
        `• Low Performers (ESG < 60): ${reportData.lowPerformers}`,
        `• Risk Alerts: ${reportData.riskAlerts}`,
        '',
        'Key Findings:',
        `• ${reportData.highPerformers > reportData.totalInvestments / 2 ? 'Strong ESG performance with majority of investments scoring high' : 'Mixed ESG performance requiring attention to low-scoring investments'}`,
        `• ${reportData.riskAlerts > 0 ? `${reportData.riskAlerts} investments require immediate attention due to elevated risk indicators` : 'Portfolio shows good risk management with no critical alerts'}`,
        `• Top sectors: ${reportData.topSectors.join(', ')}`,
        '',
        'Strategic Implications:',
        `The portfolio demonstrates ${reportData.avgESGScore >= 70 ? 'strong' : reportData.avgESGScore >= 50 ? 'moderate' : 'weak'} ESG integration with opportunities for ${reportData.lowPerformers > 0 ? 'improvement in underperforming areas' : 'further optimization'}`
      ];

      executiveSummary.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.startsWith('•')) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(34, 197, 94);
          pdf.text(line, margin, yPosition);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'normal');
        } else if (line.includes(':')) {
          pdf.setFont(undefined, 'bold');
          pdf.text(line, margin, yPosition);
          pdf.setFont(undefined, 'normal');
        } else {
          pdf.text(line, margin, yPosition);
        }
        yPosition += 5;
      });

      yPosition += 15;

      // 3. Data Review
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('3. Data Review', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const dataReview = [
        'Portfolio Composition:',
        `The portfolio consists of ${reportData.totalInvestments} active investments across various sectors and investment types.`,
        '',
        'Sector Distribution:',
        ...reportData.sectorBreakdown.map((sector: any) => 
          `• ${sector.sector}: ${sector.count} investments, ${sector.totalValue.toLocaleString()} ${reportData.currency} (${((sector.totalValue / reportData.totalValue) * 100).toFixed(1)}%)`
        ),
        '',
        'Investment Types:',
        ...reportData.investmentTypes.map((type: any) => 
          `• ${type.name}: ${type.count} investments, ${type.value.toLocaleString()} ${reportData.currency} (${((type.value / reportData.totalValue) * 100).toFixed(1)}%)`
        ),
        '',
        'ESG Score Distribution:',
        `• High Performers (80-100): ${reportData.highPerformers} investments`,
        `• Medium Performers (60-79): ${reportData.totalInvestments - reportData.highPerformers - reportData.lowPerformers} investments`,
        `• Low Performers (0-59): ${reportData.lowPerformers} investments`
      ];

      dataReview.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.startsWith('•')) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(34, 197, 94);
          pdf.text(line, margin, yPosition);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'normal');
        } else if (line.includes(':')) {
          pdf.setFont(undefined, 'bold');
          pdf.text(line, margin, yPosition);
          pdf.setFont(undefined, 'normal');
        } else {
          pdf.text(line, margin, yPosition);
        }
        yPosition += 5;
      });

      yPosition += 15;

      // 4. Data Analysis
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('4. Data Analysis', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const dataAnalysis = [
        'ESG Performance Analysis:',
        `The portfolio achieves an average ESG score of ${reportData.avgESGScore.toFixed(1)}, which is ${reportData.avgESGScore >= 80 ? 'excellent' : reportData.avgESGScore >= 60 ? 'good' : 'below average'} performance.`,
        '',
        'ESG Component Breakdown:',
        ...reportData.esgBreakdown.map((component: any) => 
          `• ${component.name}: ${component.value} (${Number(component.value) >= 80 ? 'Excellent' : Number(component.value) >= 60 ? 'Good' : 'Needs Improvement'})`
        ),
        '',
        'Risk Assessment:',
        `• High-risk investments: ${reportData.riskAnalysis.filter((r: any) => r.riskLevel === 'High').length}`,
        `• Medium-risk investments: ${reportData.riskAnalysis.filter((r: any) => r.riskLevel === 'Medium').length}`,
        `• Low-risk investments: ${reportData.riskAnalysis.filter((r: any) => r.riskLevel === 'Low').length}`,
        '',
        'Sector Performance:',
        ...reportData.sectorBreakdown.map((sector: any) => 
          `• ${sector.sector}: Average ESG score ${sector.avgESGScore.toFixed(1)}`
        )
      ];

      dataAnalysis.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.startsWith('•')) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(34, 197, 94);
          pdf.text(line, margin, yPosition);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'normal');
        } else if (line.includes(':')) {
          pdf.setFont(undefined, 'bold');
          pdf.text(line, margin, yPosition);
          pdf.setFont(undefined, 'normal');
        } else {
          pdf.text(line, margin, yPosition);
        }
        yPosition += 5;
      });

      yPosition += 10;

      // Add ESG Analytics Charts
      pdf.setFontSize(14);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('ESG Analytics Charts', margin, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      yPosition += 10;

      // Chart 1: ESG Scores by Sector
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 15, 'F');
      pdf.setFontSize(12);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Chart 1: ESG Scores by Sector', margin, yPosition + 5);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      yPosition += 15;
      if (!reportData.sectorBreakdown || reportData.sectorBreakdown.length === 0) {
        pdf.setFontSize(10);
        pdf.text('No sector ESG data available.', margin, yPosition);
        yPosition += 12;
      } else if (sectorChartRef.current) {
        yPosition = await captureAndAddChartImage(sectorChartRef.current, yPosition);
      }

      // Add chart description
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      const hasSectors = Array.isArray(reportData.sectorBreakdown) && reportData.sectorBreakdown.length > 0;
      const bestSector = hasSectors
        ? reportData.sectorBreakdown.reduce(
            (max: any, sector: any) => (Number(sector.avgESGScore) > Number(max.avgESGScore) ? sector : max),
            reportData.sectorBreakdown[0]
          )
        : null;
      const worstSector = hasSectors
        ? reportData.sectorBreakdown.reduce(
            (min: any, sector: any) => (Number(sector.avgESGScore) < Number(min.avgESGScore) ? sector : min),
            reportData.sectorBreakdown[0]
          )
        : null;

      const chart1Description = [
        'This chart shows the average ESG scores across different sectors in the portfolio.',
        'Key observations:',
        ...(hasSectors
          ? reportData.sectorBreakdown.map((sector: any) =>
              `• ${sector.sector}: ${Number(sector.avgESGScore || 0).toFixed(1)} ESG score (${sector.count} investments)`
            )
          : ['• No sector data available']
        ),
        '',
        hasSectors
          ? `The ${bestSector.sector} sector shows the highest ESG performance, while ${worstSector.sector} requires attention for ESG improvement.`
          : 'Sector performance comparison is unavailable due to lack of data.'
      ];

      chart1Description.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        const lines = pdf.splitTextToSize(line, contentWidth);
        lines.forEach(textLine => {
          pdf.text(textLine, margin, yPosition);
          yPosition += 4;
        });
        yPosition += 2;
      });

      yPosition += 10;

      // Chart 2: ESG Component Breakdown
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 15, 'F');
      pdf.setFontSize(12);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Chart 2: ESG Component Breakdown', margin, yPosition + 5);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      yPosition += 15;
      if (!reportData.esgBreakdown || reportData.esgBreakdown.length === 0) {
        pdf.setFontSize(10);
        pdf.text('No ESG component data available.', margin, yPosition);
        yPosition += 12;
      } else if (esgChartRef.current) {
        yPosition = await captureAndAddChartImage(esgChartRef.current, yPosition);
      }

      // Add chart description
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      const chart2Description = [
        'This chart breaks down the portfolio\'s ESG performance by individual components.',
        'Component Analysis:',
        ...reportData.esgBreakdown.map((component: any) => 
          `• ${component.name}: ${component.value} (${Number(component.value) >= 80 ? 'Excellent' : Number(component.value) >= 60 ? 'Good' : 'Needs Improvement'})`
        ),
        '',
        'This breakdown helps identify specific areas of strength and opportunities for improvement within the ESG framework.'
      ];

      chart2Description.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        const lines = pdf.splitTextToSize(line, contentWidth);
        lines.forEach(textLine => {
          pdf.text(textLine, margin, yPosition);
          yPosition += 4;
        });
        yPosition += 2;
      });

      yPosition += 10;

      // Chart 3: Investment Allocation by Type
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 15, 'F');
      pdf.setFontSize(12);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Chart 3: Investment Allocation by Type', margin, yPosition + 5);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      yPosition += 15;
      const totalValue = Array.isArray(reportData.investmentTypes)
        ? reportData.investmentTypes.reduce((sum: number, type: any) => sum + (Number(type.value) || 0), 0)
        : 0;
      if (!reportData.investmentTypes || reportData.investmentTypes.length === 0 || totalValue <= 0) {
        pdf.setFontSize(10);
        pdf.text('No investment type allocation data available.', margin, yPosition);
        yPosition += 12;
      } else if (invTypeChartRef.current) {
        yPosition = await captureAndAddChartImage(invTypeChartRef.current, yPosition);
      }

      // Add legend
      yPosition += 80;
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Legend:', margin, yPosition);
      yPosition += 8;

      reportData.investmentTypes.forEach((type: any, index: number) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }

        const percentage = (type.value / totalValue) * 100;
        pdf.setFontSize(8);
        pdf.text(`• ${type.name}: ${percentage.toFixed(1)}% (${type.value.toLocaleString()} ${reportData.currency})`, margin + 5, yPosition);
        yPosition += 5;
      });

      // Add chart description
      yPosition += 10;
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      const mostConcentratedType = (reportData.investmentTypes && reportData.investmentTypes.length > 0)
        ? reportData.investmentTypes.reduce(
            (max: any, type: any) => (Number(type.value) > Number(max.value) ? type : max),
            reportData.investmentTypes[0]
          ).name
        : 'N/A';

      const chart3Description = [
        'This chart shows the distribution of investments across different investment types.',
        'Allocation Insights:',
        `• Total portfolio value: ${totalValue.toLocaleString()} ${reportData.currency}`,
        `• Number of investment types: ${reportData.investmentTypes.length}`,
        `• Most concentrated type: ${mostConcentratedType}`,
        '',
        'This visualization helps understand portfolio diversification across investment vehicles and strategies.'
      ];

      chart3Description.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        const lines = pdf.splitTextToSize(line, contentWidth);
        lines.forEach(textLine => {
          pdf.text(textLine, margin, yPosition);
          yPosition += 4;
        });
        yPosition += 2;
      });

      yPosition += 10;

      // Chart 4: Sector Value Distribution
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 15, 'F');
      pdf.setFontSize(12);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('Chart 4: Sector Value Distribution', margin, yPosition + 5);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      yPosition += 15;
      const totalSectorValue = Array.isArray(reportData.sectorBreakdown)
        ? reportData.sectorBreakdown.reduce((sum: number, sector: any) => sum + (Number(sector.totalValue) || 0), 0)
        : 0;
      if (!reportData.sectorBreakdown || reportData.sectorBreakdown.length === 0 || totalSectorValue <= 0) {
        pdf.setFontSize(10);
        pdf.text('No sector value distribution data available.', margin, yPosition);
        yPosition += 12;
      } else if (sectorValueChartRef.current) {
        yPosition = await captureAndAddChartImage(sectorValueChartRef.current, yPosition);
      }

      // Add legend
      yPosition += 80;
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Sector Legend:', margin, yPosition);
      yPosition += 8;

      reportData.sectorBreakdown.forEach((sector: any, index: number) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }

        const percentage = (sector.totalValue / totalSectorValue) * 100;
        pdf.setFontSize(8);
        pdf.text(`• ${sector.sector}: ${percentage.toFixed(1)}% (${sector.totalValue.toLocaleString()} ${reportData.currency})`, margin + 5, yPosition);
        yPosition += 5;
      });

      // Add chart description
      yPosition += 10;
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      const chart4Description = [
        'This chart shows the distribution of portfolio value across different sectors.',
        'Sector Concentration Analysis:',
        `• Total portfolio value: ${totalSectorValue.toLocaleString()} ${reportData.currency}`,
        `• Number of sectors: ${reportData.sectorBreakdown.length}`,
        `• Most concentrated sector: ${reportData.sectorBreakdown && reportData.sectorBreakdown.length > 0 ? reportData.sectorBreakdown.reduce((max: any, sector: any) => (Number(sector.totalValue) > Number(max.totalValue) ? sector : max), reportData.sectorBreakdown[0]).sector : 'N/A'}`,
        '',
        'This visualization helps assess sector diversification and identify concentration risks or opportunities.'
      ];

      chart4Description.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        const lines = pdf.splitTextToSize(line, contentWidth);
        lines.forEach(textLine => {
          pdf.text(textLine, margin, yPosition);
          yPosition += 4;
        });
        yPosition += 2;
      });

      yPosition += 15;

      // 5. Discussion of Analysed Data
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('5. Discussion of Analysed Data', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const discussion = [
        'Portfolio Strengths:',
        `• ${reportData.highPerformers > 0 ? `${reportData.highPerformers} investments demonstrate excellent ESG performance` : 'Limited high-performing ESG investments'}`,
        `• ${reportData.avgESGScore >= 70 ? 'Strong overall ESG integration across the portfolio' : 'Moderate ESG integration with room for improvement'}`,
        `• Diversified sector exposure with ${reportData.sectorBreakdown.length} different sectors represented`,
        '',
        'Areas for Improvement:',
        `• ${reportData.lowPerformers > 0 ? `${reportData.lowPerformers} investments require immediate attention to improve ESG scores` : 'No low-performing investments identified'}`,
        `• ${reportData.riskAlerts > 0 ? `${reportData.riskAlerts} investments show elevated risk indicators requiring monitoring` : 'Good risk management with no critical alerts'}`,
        '',
        'Market Position:',
        `The portfolio's average ESG score of ${reportData.avgESGScore.toFixed(1)} positions it ${reportData.avgESGScore >= 80 ? 'among industry leaders' : reportData.avgESGScore >= 60 ? 'in line with market standards' : 'below market standards'} in terms of sustainability performance.`,
        '',
        'Risk-Return Profile:',
        `The current ESG integration strategy ${reportData.riskAlerts === 0 ? 'effectively manages sustainability risks' : 'requires enhanced risk monitoring and mitigation strategies'}.`
      ];

      discussion.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.startsWith('•')) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(34, 197, 94);
          pdf.text(line, margin, yPosition);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'normal');
        } else if (line.includes(':')) {
          pdf.setFont(undefined, 'bold');
          pdf.text(line, margin, yPosition);
          pdf.setFont(undefined, 'normal');
        } else {
          pdf.text(line, margin, yPosition);
        }
        yPosition += 5;
      });

      yPosition += 15;

      // 6. Conclusion
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('6. Conclusion', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const conclusion = [
        `The portfolio demonstrates ${reportData.avgESGScore >= 70 ? 'strong' : reportData.avgESGScore >= 50 ? 'moderate' : 'weak'} ESG integration with a total value of ${reportData.totalValue.toLocaleString()} ${reportData.currency} across ${reportData.totalInvestments} investments.`,
        '',
        'Key Achievements:',
        `• ${reportData.highPerformers} high-performing ESG investments`,
        `• Average ESG score of ${reportData.avgESGScore.toFixed(1)}`,
        `• Diversified sector exposure`,
        '',
        'Challenges Identified:',
        `• ${reportData.lowPerformers} investments require ESG improvement`,
        `• ${reportData.riskAlerts} risk alerts need attention`,
        '',
        'Overall Assessment:',
        `The portfolio ${reportData.avgESGScore >= 70 ? 'successfully integrates ESG considerations' : 'shows progress in ESG integration but requires continued focus'} and demonstrates ${reportData.riskAlerts === 0 ? 'effective risk management' : 'opportunities for enhanced risk mitigation'}.`
      ];

      conclusion.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.startsWith('•')) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(34, 197, 94);
          pdf.text(line, margin, yPosition);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'normal');
        } else if (line.includes(':')) {
          pdf.setFont(undefined, 'bold');
          pdf.text(line, margin, yPosition);
          pdf.setFont(undefined, 'normal');
        } else {
          pdf.text(line, margin, yPosition);
        }
        yPosition += 5;
      });

      yPosition += 15;

      // 7. Recommendations
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin - 5, yPosition - 5, contentWidth + 10, 25, 'F');
      
      pdf.setFontSize(16);
      pdf.setTextColor(34, 197, 94);
      pdf.setFont(undefined, 'bold');
      pdf.text('7. Recommendations', margin, yPosition + 8);
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 20;
      pdf.setFontSize(10);
      
      const recommendations = [
        'Immediate Actions (Next 30 Days):',
        `• ${reportData.lowPerformers > 0 ? `Review and develop improvement plans for ${reportData.lowPerformers} low-performing investments` : 'Maintain current high performance standards'}`,
        `• ${reportData.riskAlerts > 0 ? `Address ${reportData.riskAlerts} risk alerts through enhanced monitoring and mitigation strategies` : 'Continue current risk management practices'}`,
        '• Establish ESG performance monitoring dashboard',
        '',
        'Short-term Initiatives (Next 90 Days):',
        '• Implement quarterly ESG review process',
        '• Develop sector-specific ESG improvement strategies',
        '• Enhance ESG scoring methodology and data collection',
        '• Conduct ESG training for investment team',
        '',
        'Long-term Strategy (Next 12 Months):',
        '• Set ESG performance targets and KPIs',
        '• Develop ESG integration framework',
        '• Establish stakeholder engagement program',
        '• Create ESG reporting and disclosure protocols',
        '',
        'Continuous Improvement:',
        '• Regular ESG performance benchmarking',
        '• Industry best practice adoption',
        '• ESG risk assessment updates',
        '• Portfolio optimization based on ESG performance'
      ];

      recommendations.forEach(line => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.startsWith('•')) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(34, 197, 94);
          pdf.text(line, margin, yPosition);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'normal');
        } else if (line.includes(':')) {
          pdf.setFont(undefined, 'bold');
          pdf.text(line, margin, yPosition);
          pdf.setFont(undefined, 'normal');
        } else {
          pdf.text(line, margin, yPosition);
        }
        yPosition += 5;
      });

      // Modern footer
      const pageCount = pdf.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFillColor(34, 197, 94);
        pdf.rect(0, 280, pageWidth, 20, 'F');
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 30, 290);
        pdf.text('Greencard Sustainability Manager - Portfolio ESG Report', margin, 290);
        pdf.setTextColor(0, 0, 0);
      }

      // Download the PDF
      const filename = `portfolio-esg-report-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      toast({
        title: "Success",
        description: "Professional PDF report generated and downloaded successfully",
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF report",
        variant: "destructive",
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-gray-200 rounded"></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Portfolio ESG Report</h3>
          <p className="text-sm text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-muted rounded p-1">
            <button
              type="button"
              className={`px-2 py-1 rounded text-sm ${pdfQuality === 'standard' ? 'bg-white shadow' : ''}`}
              onClick={() => setPdfQuality('standard')}
              disabled={generatingPDF}
              title="High quality (larger file)"
            >Standard</button>
            <button
              type="button"
              className={`px-2 py-1 rounded text-sm ${pdfQuality === 'compact' ? 'bg-white shadow' : ''}`}
              onClick={() => setPdfQuality('compact')}
              disabled={generatingPDF}
              title="Smaller file (compressed)"
            >Compact</button>
          </div>
          <Button 
            onClick={generateProfessionalPDF} 
            disabled={generatingPDF}
            className="flex items-center gap-2"
          >
            {generatingPDF ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4" />
                Generate PDF Report
              </>
            )}
          </Button>
        </div>
      </div>
      {/* Off-screen charts for PDF capture */}
      <div className="absolute -left-[9999px] top-0">
        <div ref={sectorChartRef} style={{ width: 800, height: 400, background: '#ffffff' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData.sectorBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="sector" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="avgESGScore" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div ref={esgChartRef} style={{ width: 800, height: 400, background: '#ffffff', marginTop: 16 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData.esgBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#4ade80" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div ref={invTypeChartRef} style={{ width: 800, height: 400, background: '#ffffff', marginTop: 16 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={reportData.investmentTypes} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={140} label>
                {reportData.investmentTypes?.map((entry: any, index: number) => (
                  <Cell key={`cell-it-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" align="center" layout="horizontal" iconType="square" />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div ref={sectorValueChartRef} style={{ width: 800, height: 400, background: '#ffffff', marginTop: 16 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={reportData.sectorBreakdown} dataKey="totalValue" nameKey="sector" cx="50%" cy="50%" outerRadius={140} label>
                {reportData.sectorBreakdown?.map((entry: any, index: number) => (
                  <Cell key={`cell-sv-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" align="center" layout="horizontal" iconType="square" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* existing visible cards and content below */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Total Investments</p>
                <p className="text-2xl font-bold">{reportData.totalInvestments}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Portfolio Value</p>
                <p className="text-2xl font-bold">{reportData.totalValue.toLocaleString()}</p>
                <p className="text-sm text-gray-500">{reportData.currency}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className={`h-5 w-5 rounded-full ${
                reportData.avgESGScore >= 80 ? 'bg-green-500' :
                reportData.avgESGScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`} />
              <div>
                <p className="text-sm text-gray-600">Avg ESG Score</p>
                <p className="text-2xl font-bold">{reportData.avgESGScore.toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <TrendingUp className="h-5 w-5" />
              High Performers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{reportData.highPerformers}</p>
              <p className="text-sm text-gray-600">ESG Score ≥ 80</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <TrendingDown className="h-5 w-5" />
              Low Performers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">{reportData.lowPerformers}</p>
              <p className="text-sm text-gray-600">ESG Score &lt; 60</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="h-5 w-5" />
              Risk Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-600">{reportData.riskAlerts}</p>
              <p className="text-sm text-gray-600">Require attention</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top Sectors</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {reportData.topSectors.map((sector, index) => (
              <Badge key={sector} variant="outline" className="text-sm">
                #{index + 1} {sector}
              </Badge>
            ))}
          </div>
          {reportData.topSectors.length === 0 && (
            <p className="text-gray-500 text-center py-4">No sector data available</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {reportData.lowPerformers > 0 && (
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Action Required:</strong> {reportData.lowPerformers} investments have ESG scores below 60. 
                  Consider reviewing these positions for improvement opportunities.
                </p>
              </div>
            )}
            {reportData.riskAlerts > 0 && (
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Risk Alert:</strong> {reportData.riskAlerts} investments show elevated risk indicators. 
                  Monitor carbon intensity and ESG compliance closely.
                </p>
              </div>
            )}
            {reportData.highPerformers > reportData.totalInvestments / 2 && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Well Done:</strong> Your portfolio shows strong ESG performance with {reportData.highPerformers} high-performing investments.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
